import { Play, Users, Target, AlertTriangle, CheckCircle2, Package } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PlanOrder {
  id: string;
  orderNumber: string;
  productName: string;
  targetQty: number;
  manpowerCount?: number | null;
  shiftTarget?: number | null;
  status: string;
  priority?: string | null;
  plannedStart?: string | null;
  materialAvailable?: boolean | null; // null = unknown, true = OK, false = shortage
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, string> = {
  pending:     'bg-gray-100 text-gray-700 border-gray-200',
  planned:     'bg-blue-50 text-blue-700 border-blue-200',
  ready:       'bg-emerald-50 text-emerald-700 border-emerald-200',
  in_progress: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  completed:   'bg-green-50 text-green-700 border-green-200',
  on_hold:     'bg-yellow-50 text-yellow-700 border-yellow-200',
  cancelled:   'bg-red-50 text-red-700 border-red-200',
};

const PRIORITY_STYLE: Record<string, string> = {
  low:      'bg-gray-100 text-gray-600',
  medium:   'bg-blue-100 text-blue-700',
  high:     'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  order: PlanOrder;
  onStart?: (order: PlanOrder) => void;
  startLabel?: string;
  compact?: boolean;
}

export function PlanOrderCard({ order, onStart, startLabel = 'Start', compact }: Props) {
  const statusCls = STATUS_STYLE[order.status] ?? 'bg-gray-100 text-gray-700 border-gray-200';
  const priorityCls = order.priority ? (PRIORITY_STYLE[order.priority] ?? 'bg-gray-100 text-gray-600') : null;

  const materialIcon =
    order.materialAvailable === true ? (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5" /> Materials OK
      </span>
    ) : order.materialAvailable === false ? (
      <span className="inline-flex items-center gap-1 text-xs text-red-700">
        <AlertTriangle className="h-3.5 w-3.5" /> Short
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Package className="h-3.5 w-3.5" /> Materials n/a
      </span>
    );

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow transition ${
        compact ? '' : 'flex flex-col gap-2'
      }`}
    >
      {/* Top row: PO number + priority badge */}
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs font-semibold text-gray-900">{order.orderNumber}</span>
        {priorityCls && order.priority && (
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${priorityCls}`}>
            {order.priority}
          </span>
        )}
      </div>

      {/* Product name */}
      <div className="truncate text-sm font-medium text-gray-800" title={order.productName}>
        {order.productName}
      </div>

      {/* Metrics row: target, manpower, shift target */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1" title="Target quantity">
          <Target className="h-3.5 w-3.5" /> {order.targetQty}
        </span>
        {order.manpowerCount != null && (
          <span className="inline-flex items-center gap-1" title="Manpower">
            <Users className="h-3.5 w-3.5" /> {order.manpowerCount}
          </span>
        )}
        {order.shiftTarget != null && (
          <span className="inline-flex items-center gap-1" title="Per-shift target">
            ST: {order.shiftTarget}
          </span>
        )}
      </div>

      {/* Bottom row: status chip + material + start btn */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <div className="flex items-center gap-2">
          <span
            className={`rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${statusCls}`}
          >
            {formatStatus(order.status)}
          </span>
          {materialIcon}
        </div>
        {onStart && (
          <button
            type="button"
            onClick={() => onStart(order)}
            className="inline-flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700"
          >
            <Play className="h-3 w-3" /> {startLabel}
          </button>
        )}
      </div>
    </div>
  );
}
